'use client'

import React, { useState } from 'react'
import Image, { type ImageProps } from 'next/image'

const ERROR_IMG_SRC =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODgiIGhlaWdodD0iODgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgc3Ryb2tlPSIjMDAwIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBvcGFjaXR5PSIuMyIgZmlsbD0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIzLjciPjxyZWN0IHg9IjE2IiB5PSIxNiIgd2lkdGg9IjU2IiBoZWlnaHQ9IjU2IiByeD0iNiIvPjxwYXRoIGQ9Im0xNiA1OCAxNi0xOCAzMiAzMiIvPjxjaXJjbGUgY3g9IjUzIiBjeT0iMzUiIHI9IjciLz48L3N2Zz4KCg=='

type Props = ImageProps & {
  /** Optional explicit fallback image (defaults to a built-in placeholder). */
  fallbackSrc?: string
}

/**
 * Primary: next/image for optimization (LCP, responsive, formats).
 * Fallback: plain <img> only when the optimized image fails to load.
 */
export function ImageWithFallback(props: Props) {
  const { fallbackSrc, onError: userOnError, alt, ...imgProps } = props
  const [failed, setFailed] = useState(false)

  if (failed) {
    // Render a simple fallback box using a plain <img>.
    return (
      <div
        className={`inline-block bg-gray-100 text-center align-middle ${'className' in imgProps ? ((imgProps as any).className ?? '') : ''}`}
        style={'style' in imgProps ? (imgProps as any).style : undefined}
        aria-label="Image fallback"
      >
        <div className="flex items-center justify-center w-full h-full">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={fallbackSrc ?? ERROR_IMG_SRC}
            alt={
              typeof alt === 'string' && alt
                ? `Fallback: ${alt}`
                : 'Fallback image'
            }
            data-original-url={String(imgProps.src)}
          />
        </div>
      </div>
    )
  }

  return (
    <Image
      {...imgProps}
      alt={alt}
      onError={(e) => {
        // Let the caller handle it too, then switch to fallback.
        try {
          userOnError?.(e)
        } finally {
          setFailed(true)
        }
      }}
    />
  )
}

export default ImageWithFallback
